import React, { useState, useEffect } from 'react';
import { crewSeatName } from '../../utils/truckCrewNames';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import {
  Route as RouteIcon,
  Print as PrintIcon,
  People as CrewIcon,
  OpenInNew as OpenIcon,
  InfoOutlined as InfoIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import TruckDetailsModal from '../Maps/TruckDetailsModal';
import AdditionalCrewField, { additionalCrewFromTruck, employeeDisplayName } from './AdditionalCrewField';
import RouteProgressChip, { ROUTE_PROGRESS_REFRESH_MS } from './RouteProgressChip';

const DAYS_OF_WEEK = [
  { value: 1, key: 'monday' },
  { value: 2, key: 'tuesday' },
  { value: 3, key: 'wednesday' },
  { value: 4, key: 'thursday' },
  { value: 5, key: 'friday' },
  { value: 6, key: 'saturday' },
  { value: 7, key: 'sunday' },
];

/**
 * Routes list page — an "all routes" table view that complements the grid in
 * Route Planning. Every active route (day_of_week x truck) is a row with its
 * stop count, driver + passenger, and quick Print / Assign Crew actions.
 *
 * Crew is sourced from the truck's current_driver_user_id /
 * current_passenger_user_id (legacy parity). Editing it here still updates
 * the truck itself, which is the single source of truth for who's on a rig.
 */
const RoutesList = () => {
  const { t } = useTranslation();

  const [trucks, setTrucks] = useState([]);
  const [slotCounts, setSlotCounts] = useState({}); // `${day}:${truckId}` -> count
  const [todayCounts, setTodayCounts] = useState({}); // truckId -> today's active-job count
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Crew edit dialog
  const [crewOpen, setCrewOpen] = useState(false);
  const [crewTruck, setCrewTruck] = useState(null);
  const [crewDriverId, setCrewDriverId] = useState('');
  const [crewPassengerId, setCrewPassengerId] = useState('');
  const [crewExtras, setCrewExtras] = useState([]);
  const [savingCrew, setSavingCrew] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Live location modal state
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveTruckId, setLiveTruckId] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveLocation, setLiveLocation] = useState(null);
  const [liveNoGps, setLiveNoGps] = useState(false);

  // TKT-420: live ahead/behind + predicted finish per truck for today,
  // recalculated from clock times on every request. Refreshed every two
  // minutes; failures just mean no chips.
  const [routeProgress, setRouteProgress] = useState({}); // truckId -> progress

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadProgress = () => {
      makeApiCall('api/routes/live-progress')
        .then((response) => (response.ok ? response.json() : { trucks: [] }))
        .then((payload) => {
          if (cancelled) return;
          const byTruck = {};
          (payload.trucks || []).forEach((truck) => {
            byTruck[truck.truck_id] = truck;
          });
          setRouteProgress(byTruck);
        })
        .catch(() => {
          // Inline chips only — never surface errors for them.
        });
    };
    loadProgress();
    const id = setInterval(loadProgress, ROUTE_PROGRESS_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [trucksRes, jobSitesRes, todayRes] = await Promise.all([
        makeApiCall('/api/routes/trucks'),
        makeApiCall('/api/routes/job-sites?is_active=true'),
        makeApiCall('/api/routes/today-active-counts'),
      ]);

      if (trucksRes.ok) {
        const trucksData = await trucksRes.json();
        setTrucks((trucksData.trucks || []).filter((tr) => tr.is_active !== false));
      } else {
        throw new Error('Failed to load trucks');
      }

      if (jobSitesRes.ok) {
        const data = await jobSitesRes.json();
        const counts = {};
        (data.job_sites || []).forEach((js) => {
          const day = js.is_custom ? js.custom_day_of_week : js.day_of_week;
          const truck = js.is_custom ? js.custom_truck_id : js.truck_id;
          if (!day || !truck) return;
          const key = `${day}:${truck}`;
          counts[key] = (counts[key] || 0) + 1;
        });
        setSlotCounts(counts);
      }

      if (todayRes.ok) {
        const data = await todayRes.json();
        setTodayCounts(data.counts || {});
      }
    } catch (err) {
      console.error(err);
      setError(t('routesList.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const refreshTrucks = async () => {
    try {
      const response = await makeApiCall('/api/routes/trucks');
      if (response.ok) {
        const data = await response.json();
        setTrucks((data.trucks || []).filter((tr) => tr.is_active !== false));
      }
    } catch (err) {
      console.error('Failed to refresh trucks', err);
    }
  };

  const openCrewDialog = async (truck) => {
    setCrewTruck(truck);
    setCrewDriverId(truck.current_driver_user_id || '');
    setCrewPassengerId(truck.current_passenger_user_id || '');
    setCrewExtras(additionalCrewFromTruck(truck));
    setCrewOpen(true);
    if (availableEmployees.length === 0 && !loadingEmployees) {
      try {
        setLoadingEmployees(true);
        const response = await makeApiCall('/api/routes/lookups/available-employees?include_subcontractors=true');
        if (response.ok) {
          const data = await response.json();
          setAvailableEmployees(data.available_employees || []);
        }
      } catch (err) {
        console.error('Failed to load employees', err);
      } finally {
        setLoadingEmployees(false);
      }
    }
  };

  const saveCrew = async () => {
    if (!crewTruck) return;
    try {
      setSavingCrew(true);
      const response = await makeApiCall(`/api/routes/trucks/${crewTruck.id}/driver`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_driver_user_id: crewDriverId || null,
          current_passenger_user_id: crewPassengerId || null,
          additional_crew_user_ids: crewExtras.map((emp) => emp.id),
        }),
      });
      if (!response.ok) throw new Error('Failed to save crew');
      await refreshTrucks();
      setCrewOpen(false);
      setSuccessMsg(t('routesList.crewSaved'));
    } catch (err) {
      console.error(err);
      setError(t('routesList.failedToSaveCrew'));
    } finally {
      setSavingCrew(false);
    }
  };

  const fetchLiveLocationForTruck = async (truckId, { showLoading = true } = {}) => {
    try {
      if (showLoading) setLiveLoading(true);
      const response = await makeApiCall('/api/gps/locations/current');
      if (!response.ok) throw new Error('Failed to fetch GPS data');
      const data = await response.json();
      const match = (data.truck_locations || []).find((tr) => tr.truck_id === truckId);
      if (!match || !match.latitude || !match.longitude) {
        setLiveLocation(match || null);
        setLiveNoGps(true);
      } else {
        setLiveLocation(match);
        setLiveNoGps(false);
      }
    } catch (err) {
      console.error('Failed to load live location', err);
      setError(t('routesList.failedToLoadLive'));
      setLiveNoGps(true);
    } finally {
      if (showLoading) setLiveLoading(false);
    }
  };

  const openLiveLocation = async (truck) => {
    setLiveTruckId(truck.id);
    setLiveLocation(null);
    setLiveNoGps(false);
    setLiveOpen(true);
    await fetchLiveLocationForTruck(truck.id);
  };

  const closeLiveLocation = () => {
    setLiveOpen(false);
    setLiveTruckId(null);
    setLiveLocation(null);
    setLiveNoGps(false);
  };

  useEffect(() => {
    if (!liveOpen || !liveTruckId) return undefined;
    const interval = setInterval(() => {
      fetchLiveLocationForTruck(liveTruckId, { showLoading: false });
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveOpen, liveTruckId]);

  const openPrintWindow = (day, truckId) => {
    window.open(
      `/schedule/print?day_of_week=${day}&truck_id=${truckId}`,
      '_blank'
    );
  };

  // Today's weekday in 1..7 (1=Monday, 7=Sunday) to match job_sites.day_of_week.
  const getTodayDow = () => {
    const d = new Date().getDay(); // 0=Sun..6=Sat
    return d === 0 ? 7 : d;
  };
  const todayDow = getTodayDow();

  // Build the row data: one per (truck, day) that has either planned stops
  // (weekly template) OR today's active jobs. A truck with jobs today but no
  // weekly slot for today's weekday gets a synthetic today-row flagged as
  // off-schedule so the dispatcher can see it.
  //
  // Sorted primarily by day-of-week (Mon..Sun), then by truck name.
  const buildRows = () => {
    const rows = [];
    const todayKey = DAYS_OF_WEEK.find((d) => d.value === todayDow)?.key || 'monday';

    trucks.forEach((truck) => {
      const todayBreakdown = todayCounts[truck.id] || { planned: 0, offSchedule: 0, total: 0 };
      const todayTotal = Number(todayBreakdown.total || 0);
      const todayOffSchedule = Number(todayBreakdown.offSchedule || 0);
      // TKT-478: snow-list visits have no weekday, so they're flagged Snow
      // rather than off-schedule.
      const todaySnow = Number(todayBreakdown.snow || 0);

      DAYS_OF_WEEK.forEach(({ value: day, key }) => {
        const stopCount = slotCounts[`${day}:${truck.id}`] || 0;
        const isToday = day === todayDow;
        if (stopCount === 0 && !(isToday && todayTotal > 0)) return;
        rows.push({
          truck,
          day,
          dayKey: key,
          stopCount,
          jobsToday: isToday ? todayTotal : null,
          offScheduleCount: isToday ? todayOffSchedule : 0,
          offScheduleRow: isToday && stopCount === 0 && todayOffSchedule > 0,
          snowCount: isToday ? todaySnow : 0,
        });
      });

      // Edge case: truck has off-schedule jobs today but no weekly slot for
      // today's weekday and we didn't already add a row above.
      const hasTodayRow = rows.some(
        (r) => r.truck.id === truck.id && r.day === todayDow
      );
      if (!hasTodayRow && todayTotal > 0) {
        rows.push({
          truck,
          day: todayDow,
          dayKey: todayKey,
          stopCount: 0,
          jobsToday: todayTotal,
          offScheduleCount: todayOffSchedule,
          offScheduleRow: todayOffSchedule > 0,
          snowCount: todaySnow,
        });
      }
    });

    return rows.sort((a, b) => {
      if (a.day !== b.day) return a.day - b.day;
      return a.truck.name.localeCompare(b.truck.name);
    });
  };

  const renderEmployeeName = (emp) => {
    const full = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
    return full || emp.username || `#${emp.id}`;
  };

  // TKT-281: fall back to the username, so a seat that is filled by a user
  // with no first/last name doesn't read as empty.
  const crewName = (truck) => ({
    driver: crewSeatName(truck, 'driver'),
    passenger: crewSeatName(truck, 'passenger'),
  });

  const rows = buildRows();
  // buildRows already prunes empty-empty (zero weekly stops AND zero today
  // jobs); anything remaining is worth showing.
  const rowsWithStops = rows;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }} data-demo-target="page-routes-list-root">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <RouteIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('routesList.pageTitle')}
        </Typography>
      </Stack>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
        {t('routesList.intro')}
      </Alert>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {!loading && rowsWithStops.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }} elevation={0} variant="outlined">
          <Typography color="text.secondary">{t('routesList.noRoutes')}</Typography>
        </Paper>
      )}

      {rowsWithStops.length > 0 && (
        // TKT-442: the table scrolls in its own box so the column headings and
        // the current day's heading stay pinned while scrolling a long list.
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '75vh' }}>
          <Table size="small" stickyHeader aria-label={t('routesList.pageTitle')}>
            <TableHead>
              <TableRow>
                <TableCell><strong>{t('routesList.day')}</strong></TableCell>
                <TableCell><strong>{t('routesList.truck')}</strong></TableCell>
                <TableCell><strong>{t('routesList.driver')}</strong></TableCell>
                <TableCell><strong>{t('routesList.passenger')}</strong></TableCell>
                <TableCell align="right">
                  <Tooltip title={t('routesList.plannedStopsHelp')}>
                    <strong>{t('routesList.stops')}</strong>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={t('routesList.jobsTodayHelp')}>
                    <strong>{t('routesList.jobsToday')}</strong>
                  </Tooltip>
                </TableCell>
                <TableCell align="right"><strong>{t('routesList.actions')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsWithStops.map(({ truck, day, dayKey, stopCount, jobsToday, offScheduleCount, offScheduleRow, snowCount }, rowIdx) => {
                const { driver, passenger } = crewName(truck);
                const isToday = day === todayDow;
                const dayHeader = rowIdx === 0 || rowsWithStops[rowIdx - 1].day !== day ? (
                  <TableRow key={`day-${day}`}>
                    <TableCell
                      colSpan={7}
                      sx={{
                        position: 'sticky',
                        top: 37,
                        zIndex: 1,
                        bgcolor: 'grey.100',
                        fontWeight: 700,
                        py: 0.75,
                      }}
                    >
                      {t(`days.${dayKey}`)}
                    </TableCell>
                  </TableRow>
                ) : null;
                return [dayHeader, (
                  <TableRow
                    key={`${truck.id}:${day}`}
                    hover
                    sx={isToday ? {
                      backgroundColor: (theme) => `${theme.palette.primary.main}12`, // ~7% alpha tint
                      borderLeft: '4px solid',
                      borderLeftColor: offScheduleRow ? 'warning.main' : 'primary.main',
                      '& td:first-of-type': { fontWeight: 600 },
                    } : undefined}
                    aria-label={isToday ? `${t(`days.${dayKey}`)} (${t('routesList.today')})` : undefined}
                  >
                    <TableCell>
                      {t(`days.${dayKey}`)}
                      {isToday && (
                        <Chip
                          label={t('routesList.today')}
                          size="small"
                          color="primary"
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      {offScheduleRow && (
                        <Chip
                          label={t('routesList.offSchedule')}
                          size="small"
                          color="warning"
                          sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      {snowCount > 0 && (
                        <Tooltip title={t('routesList.snowVisitsHelp', { count: snowCount })}>
                          <Chip
                            label={t('routesList.snowChip')}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }}
                          />
                        </Tooltip>
                      )}
                      {isToday && (
                        <RouteProgressChip progress={routeProgress[truck.id]} sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>{truck.name}</TableCell>
                    <TableCell>
                      {driver || (
                        <Typography variant="body2" color="text.disabled" component="span">
                          {t('routesList.noDriver')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {passenger || (
                        <Typography variant="body2" color="text.disabled" component="span">
                          {t('routesList.noPassenger')}
                        </Typography>
                      )}
                      {additionalCrewFromTruck(truck).length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('routeSetup.additionalCrewInline', {
                            names: additionalCrewFromTruck(truck).map(employeeDisplayName).join(', '),
                          })}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {stopCount > 0 ? (
                        <Chip label={stopCount} size="small" color="primary" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.disabled" component="span">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {isToday ? (
                        <Tooltip
                          title={
                            offScheduleCount > 0
                              ? t('routesList.offScheduleHelp', { count: offScheduleCount })
                              : ''
                          }
                        >
                          <Chip
                            label={jobsToday ?? 0}
                            size="small"
                            color={offScheduleCount > 0 ? 'warning' : 'primary'}
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled" component="span">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={t('routesList.liveLocation')}>
                          <IconButton
                            size="small"
                            onClick={() => openLiveLocation(truck)}
                            aria-label={t('routesList.liveLocation')}
                            color="primary"
                          >
                            <MapIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('routesList.printSchedule')}>
                          <IconButton
                            size="small"
                            onClick={() => openPrintWindow(day, truck.id)}
                            aria-label={t('routesList.printSchedule')}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('routesList.assignCrew')}>
                          <IconButton
                            size="small"
                            onClick={() => openCrewDialog(truck)}
                            aria-label={t('routesList.assignCrew')}
                          >
                            <CrewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )];
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Crew dialog (same pattern as Route Setup slot modal) */}
      <Dialog
        open={crewOpen}
        onClose={() => setCrewOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="routes-list-crew-title"
      >
        <DialogTitle id="routes-list-crew-title">
          <Stack direction="row" alignItems="center" spacing={1}>
            <CrewIcon color="primary" />
            <span>
              {t('routesList.assignCrew')} — {crewTruck?.name || ''}
            </span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('routesList.crewTruckLevelWarning')}
          </Alert>

          {loadingEmployees && <LinearProgress sx={{ mb: 2 }} />}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="routes-list-driver-label">{t('routesList.driver')}</InputLabel>
            <Select
              labelId="routes-list-driver-label"
              value={crewDriverId}
              label={t('routesList.driver')}
              onChange={(e) => setCrewDriverId(e.target.value)}
              inputProps={{ 'aria-label': t('routesList.driver') }}
            >
              <MenuItem value="">
                <em>{t('routesList.noDriver')}</em>
              </MenuItem>
              {availableEmployees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {renderEmployeeName(emp)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="routes-list-passenger-label">{t('routesList.passenger')}</InputLabel>
            <Select
              labelId="routes-list-passenger-label"
              value={crewPassengerId}
              label={t('routesList.passenger')}
              onChange={(e) => setCrewPassengerId(e.target.value)}
              inputProps={{ 'aria-label': t('routesList.passenger') }}
            >
              <MenuItem value="">
                <em>{t('routesList.noPassenger')}</em>
              </MenuItem>
              {availableEmployees.map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {renderEmployeeName(emp)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <AdditionalCrewField
            employees={availableEmployees}
            value={crewExtras}
            onChange={setCrewExtras}
            driverId={crewDriverId}
            passengerId={crewPassengerId}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrewOpen(false)} disabled={savingCrew}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={saveCrew}
            disabled={savingCrew}
            startIcon={savingCrew ? <CircularProgress size={16} /> : null}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Live location modal: reuses the same full-featured TruckDetailsModal
          shown on the Live Tracking page. If the truck has no GPS fix yet we
          render a small "no data" dialog instead of the map. */}
      {liveOpen && liveLoading && !liveLocation && (
        <Dialog
          open
          onClose={closeLiveLocation}
          maxWidth="xs"
          fullWidth
          aria-labelledby="routes-list-live-loading-title"
        >
          <DialogTitle id="routes-list-live-loading-title">
            <Stack direction="row" alignItems="center" spacing={1}>
              <MapIcon color="primary" />
              <span>{t('routesList.liveLocation')}</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                {t('routesList.loadingLive')}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeLiveLocation}>{t('common.close')}</Button>
          </DialogActions>
        </Dialog>
      )}

      {liveOpen && !liveLoading && liveNoGps && (
        <Dialog
          open
          onClose={closeLiveLocation}
          maxWidth="xs"
          fullWidth
          aria-labelledby="routes-list-live-nogps-title"
        >
          <DialogTitle id="routes-list-live-nogps-title">
            <Stack direction="row" alignItems="center" spacing={1}>
              <MapIcon color="primary" />
              <span>
                {t('routesList.liveLocation')}
                {liveLocation?.truck_name ? ` \u2014 ${liveLocation.truck_name}` : ''}
              </span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Alert severity="info">{t('routesList.noGpsData')}</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeLiveLocation}>{t('common.close')}</Button>
          </DialogActions>
        </Dialog>
      )}

      {liveOpen && liveLocation && !liveNoGps && (
        <TruckDetailsModal
          open={liveOpen}
          onClose={closeLiveLocation}
          truck={liveLocation}
        />
      )}
    </Box>
  );
};

export default RoutesList;
